import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { services } from '@/mocks/site';

const rowImages: Record<string, string> = {
  'wellness-care': 'https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20small%20tabby%20cat%20being%20gently%20examined%20by%20a%20calm%20veterinarian%20hands%20in%20a%20bright%20modern%20warm%20veterinary%20exam%20room%20with%20natural%20daylight%2C%20soft%20off%20white%20background%2C%20quiet%20focused%20wellness%20visit%2C%20natural%20skin%20tones%2C%20intimate%20real%20clinical%20moment&width=540&height=660&seq=svc-wellness-1&orientation=portrait',
  'dental-care': 'https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20veterinary%20professional%20carefully%20checking%20the%20teeth%20of%20a%20medium%20dog%20in%20a%20bright%20clean%20modern%20veterinary%20clinic%20with%20soft%20natural%20daylight%2C%20warm%20off%20white%20wall%20background%2C%20gentle%20human%20animal%20interaction%2C%20quiet%20focused%20real%20dental%20care%20moment&width=540&height=660&seq=svc-dental-1&orientation=portrait',
  'diagnostics': 'https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20veterinarian%20reviewing%20a%20digital%20xray%20on%20a%20monitor%20in%20a%20bright%20modern%20warm%20veterinary%20clinic%20with%20soft%20natural%20daylight%2C%20calm%20focused%20professional%20moment%2C%20off%20white%20background%20with%20subtle%20warm%20accents%2C%20real%20clinical%20environment&width=540&height=660&seq=svc-diag-1&orientation=portrait',
  'surgery': 'https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20small%20dog%20resting%20comfortably%20on%20a%20warm%20blanket%20in%20a%20recovery%20area%20of%20a%20bright%20modern%20veterinary%20clinic%20with%20soft%20natural%20daylight%2C%20quiet%20calm%20post%20operative%20care%20scene%2C%20warm%20off%20white%20background%2C%20real%20clinic%20setting&width=540&height=660&seq=svc-surgery-1&orientation=portrait',
};

// Fixed organic crop state per service — designed, not random
const activeShapePerService: Record<string, string> = {
  'wellness-care': '62% 38% 58% 42% / 44% 60% 40% 56%', // wide, relaxed horizontal
  'dental-care':   '48% 52% 46% 54% / 60% 40% 60% 40%', // compact, slightly taller rounded
  'diagnostics':   '68% 32% 52% 48% / 40% 62% 38% 60%', // asymmetrical diagonal oval
  'surgery':       '52% 48% 50% 50% / 54% 46% 56% 44%', // fuller, grounded, stable
};

const idleShape = '58% 42% 55% 45% / 48% 56% 44% 52%';

export default function ServicesIndex() {
  const top = services.slice(0, 4);
  const [active, setActive] = useState<string>(top[0]?.slug ?? '');

  useEffect(() => {
    // Warm decode of all row images so first hover / anchor arrival doesn't
    // pay a decode cost mid-scroll.
    Object.values(rowImages).forEach((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
  }, []);

  return (
    <section id="services" className="py-24 md:py-32 bg-white scroll-mt-[96px]">
      <div className="max-w-container mx-auto px-5 md:px-12">
        <div className="max-w-text">
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Our Services</div>
          <h2 className="mt-3 font-display font-bold text-[44px] md:text-[58px] leading-[1.02] text-cocoa">
            Modern care, organized around your pet's life.
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
          {top.map((s) => {
            const isActive = active === s.slug;
            const shape = isActive ? activeShapePerService[s.slug] : idleShape;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                onMouseEnter={() => setActive(s.slug)}
                onFocus={() => setActive(s.slug)}
                className={`group relative flex flex-col md:flex-row items-stretch gap-6 p-5 md:p-6 rounded-[32px] border transition-colors duration-500 cursor-pointer ${
                  isActive ? 'bg-yellowSoft/70 border-cocoa/25' : 'bg-warm border-line'
                }`}
              >
                <div className="relative w-full md:w-[46%] shrink-0 aspect-[4/3]">
                  <div
                    className="relative w-full h-full overflow-hidden bg-line"
                    style={{
                      borderRadius: shape,
                      transition: 'border-radius 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                    }}
                  >
                    <img
                      src={rowImages[s.slug]}
                      alt={s.title}
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover object-center transform-gpu"
                      style={{
                        transform: isActive ? 'scale(1.045)' : 'scale(1)',
                        opacity: isActive ? 1 : 0.94,
                        transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1), opacity 500ms ease-out',
                      }}
                    />
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-between py-2 md:py-4 pr-2">
                  <div>
                    <div className="text-[12px] font-semibold tracking-[0.12em] uppercase text-muted">{s.index}</div>
                    <h3 className={`mt-3 font-display font-semibold text-[26px] md:text-[30px] leading-tight text-cocoa transition-transform duration-500 ${isActive ? 'md:translate-x-0.5' : ''}`}>
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-ink/75 leading-relaxed max-w-[380px]">{s.short}</p>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-cocoa">
                    Learn more
                    <i className={`ri-arrow-right-up-line text-[16px] transition-transform duration-500 ${isActive ? '-translate-y-0.5 translate-x-0.5' : ''}`}></i>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12">
          <Link to="/services" className="inline-flex items-center gap-2 text-[15px] font-semibold text-cocoa hover:text-careDark cursor-pointer">
            View All Services
            <i className="ri-arrow-right-line text-[16px]"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}